import * as React from "react";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import { motion, AnimatePresence } from "framer-motion";

import { cn } from "@/lib/utils";

const TooltipProvider = TooltipPrimitive.Provider;

const Tooltip = TooltipPrimitive.Root;

const TooltipTrigger = TooltipPrimitive.Trigger;

const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content> & {
    variant?: "default" | "info" | "success" | "warning";
  }
>(({ className, sideOffset = 6, variant = "default", children, ...props }, ref) => {
  const variantStyles = {
    default: "bg-popover text-popover-foreground border-border",
    info: "bg-primary text-primary-foreground border-primary/20",
    success: "bg-green-600 text-white border-green-500/20",
    warning: "bg-amber-500 text-white border-amber-400/20",
  };

  return (
    <TooltipPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      asChild
      {...props}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.92, y: props.side === "bottom" ? -4 : props.side === "top" ? 4 : 0, x: props.side === "left" ? 4 : props.side === "right" ? -4 : 0 }}
        animate={{ opacity: 1, scale: 1, y: 0, x: 0 }}
        exit={{ opacity: 0, scale: 0.92 }}
        transition={{ 
          type: "spring", 
          stiffness: 500, 
          damping: 30,
          mass: 0.8
        }}
        className={cn(
          "z-50 overflow-hidden rounded-lg border px-3 py-2 text-sm shadow-lg",
          "backdrop-blur-sm",
          variantStyles[variant],
          className,
        )}
      >
        {/* Animated gradient overlay */}
        <motion.div
          className="absolute inset-0 opacity-10 pointer-events-none"
          style={{
            background: "linear-gradient(135deg, transparent 0%, currentColor 50%, transparent 100%)",
          }}
          initial={{ x: "-100%" }}
          animate={{ x: "200%" }}
          transition={{
            duration: 1.5,
            delay: 0.2,
            ease: "easeInOut",
          }}
        />
        
        {/* Content with subtle entrance */}
        <motion.span
          className="relative z-10 block"
          initial={{ opacity: 0, y: 2 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05, duration: 0.2 }}
        >
          {children}
        </motion.span>
      </motion.div>
    </TooltipPrimitive.Content>
  );
});
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

// Enhanced tooltip with icon support
interface AnimatedTooltipProps {
  content: React.ReactNode;
  children: React.ReactNode;
  variant?: "default" | "info" | "success" | "warning";
  side?: "top" | "right" | "bottom" | "left";
  delayDuration?: number;
}

const AnimatedTooltip: React.FC<AnimatedTooltipProps> = ({
  content,
  children,
  variant = "default",
  side = "top",
  delayDuration = 200,
}) => {
  return (
    <TooltipProvider delayDuration={delayDuration}>
      <Tooltip>
        <TooltipTrigger asChild>
          <motion.span
            className="inline-flex cursor-help"
            whileHover={{ scale: 1.05 }}
            transition={{ type: "spring", stiffness: 400, damping: 25 }}
          >
            {children}
          </motion.span>
        </TooltipTrigger>
        <TooltipContent variant={variant} side={side}>
          {content}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider, AnimatedTooltip };
